import { useEffect, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { Toaster } from 'sonner';

import '@/i18n/config';
import { queryClient } from '@/lib/query-client';
import { IdleLogoutProvider } from '@/components/auth/IdleLogoutProvider';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useSessionRevalidation } from '@/features/auth/hooks/useSession';
import { useChatChangedListener } from '@/features/chat/hooks/useChatChangedListener';
import { useChatMessageSound } from '@/features/chat/hooks/useChatMessageSound';
import { IncomingCallDialog } from '@/features/portal-helpline/components/IncomingCallDialog';
import { useHelplineAvailabilityListener } from '@/features/portal-helpline/hooks/useHelplineAvailabilityListener';
import { useIncomingCallListener } from '@/features/portal-helpline/hooks/useIncomingCallListener';
import { useSessionChangedListener } from '@/features/sessions/hooks/useSessionChangedListener';
import { useRedFlagChangedListener } from '@/features/red-flags/hooks/useRedFlagChangedListener';
import { useTelephonyPoolListener } from '@/features/telephony/hooks';
import { RealtimeProvider } from '@/features/realtime/RealtimeProvider';
import { startCrossTabAuthSync } from '@/lib/auth/crossTabAuth';
import { useAuthStore } from '@/stores/authStore';

import { router } from './router';

interface AppProvidersProps {
  children?: ReactNode;
}

/**
 * Subscribes to realtime UserEvents and renders any always-on overlays
 * (incoming-call dialog, future toasts, …). Lives inside the
 * RealtimeProvider so it can call useRealtimeEvent hooks.
 *
 * Each listener hook is responsible for short-circuiting on the wrong role
 * — the WS itself stays open for everyone (so client-surface events can
 * land too in the future).
 */
/**
 * Re-validates the persisted session against /auth/me once per boot. Storage
 * holds whatever the User looked like at login, so a session established before
 * a deploy never learns about fields added since — `ssoProvider` among them,
 * which decides where logout sends a federated student. Must live inside the
 * QueryClientProvider, hence a component rather than a call in AppProviders.
 */
function SessionRevalidation() {
  useSessionRevalidation();
  return null;
}

function RealtimeFeatureBindings() {
  useIncomingCallListener();
  useSessionChangedListener();
  useHelplineAvailabilityListener();
  useRedFlagChangedListener();
  useTelephonyPoolListener();
  useChatChangedListener();
  useChatMessageSound();
  return <IncomingCallDialog />;
}

export function AppProviders(_props: AppProvidersProps) {
  useEffect(() => {
    useAuthStore.getState().rehydrate();
    const token = useAuthStore.getState().accessToken;
    if (token) {
      void import('@/lib/auth/tokenRefresh').then(({ scheduleProactiveRefresh }) => {
        scheduleProactiveRefresh(token);
      });
    }
    const stopCrossTab = startCrossTabAuthSync();
    return () => {
      stopCrossTab();
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SessionRevalidation />
        <RealtimeProvider>
          <IdleLogoutProvider>
            <RouterProvider router={router} />
            <RealtimeFeatureBindings />
          </IdleLogoutProvider>
        </RealtimeProvider>
        <Toaster richColors closeButton position="top-right" />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
